import React, { useState } from 'react';
import { Star, MessageCircle, CheckCircle2 } from 'lucide-react';

const WHATSAPP = '918889616972';

// WhatsApp-style testimonials — feels authentic to Instagram/WhatsApp audience
const testimonials = [
  {
    id: 1,
    name: 'Priya S.',
    initials: 'PS',
    location: 'Mumbai',
    rating: 5,
    text: 'Got my cat\'s anime portrait on a hoodie and I\'m obsessed! The quality is amazing and the artwork captured her personality perfectly.',
    product: 'Pet Anime Hoodie',
    time: '2 days ago',
    gradient: 'from-pink-400 to-rose-500',
    verified: true,
  },
  {
    id: 2,
    name: 'Rahul M.',
    initials: 'RM',
    location: 'Pune',
    rating: 5,
    text: 'Ordered matching Ghibli-style hoodies for our anniversary. My girlfriend was literally speechless. 10/10 would order again!!',
    product: 'Couple Anime Set',
    time: '1 week ago',
    gradient: 'from-blue-400 to-indigo-500',
    verified: true,
  },
  {
    id: 3,
    name: 'Sneha K.',
    initials: 'SK',
    location: 'Bangalore',
    rating: 5,
    text: 'The team was super helpful on WhatsApp! Shared preview within 24 hours and the final print exceeded my expectations. Great communication 🙌',
    product: 'Self Custom T-Shirt',
    time: '3 days ago',
    gradient: 'from-violet-400 to-purple-500',
    verified: true,
  },
  {
    id: 4,
    name: 'Arjun P.',
    initials: 'AP',
    location: 'Delhi',
    rating: 5,
    text: 'Ordered 50 custom tees for our startup team outing. Everyone loved them! Fast turnaround and great bulk pricing. Our entire team was asking for more.',
    product: 'Corporate Bulk Order',
    time: '2 weeks ago',
    gradient: 'from-emerald-400 to-teal-500',
    verified: true,
  },
];

const StarRating = ({ count }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        className={`w-3.5 h-3.5 ${i < count ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`}
      />
    ))}
  </div>
);

// WhatsApp-style message bubble card
const WhatsAppCard = ({ review }) => (
  <div className="bg-white rounded-2xl shadow-soft p-4 hover:shadow-soft-lg transition-all duration-300 border border-gray-50">
    {/* Header */}
    <div className="flex items-start gap-3 mb-3">
      <div
        className={`w-10 h-10 rounded-full bg-gradient-to-br ${review.gradient} flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}
      >
        {review.initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-semibold text-gray-900">{review.name}</span>
          {review.verified && (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 fill-green-500 flex-shrink-0" />
          )}
          <span className="text-xs text-gray-400 ml-auto">{review.time}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <StarRating count={review.rating} />
          <span className="text-xs text-gray-400">• {review.location}</span>
        </div>
      </div>
    </div>

    {/* WhatsApp-style bubble */}
    <div
      className="relative rounded-2xl rounded-tl-sm p-3 text-sm text-gray-700 leading-relaxed"
      style={{ background: '#DCF8C6', color: '#1a1a1a' }}
    >
      {review.text}
      {/* Read receipt double tick */}
      <span className="float-right ml-2 mt-1 text-[10px] text-gray-500">✓✓</span>
    </div>

    {/* Product tag */}
    <div className="mt-3 flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-[hsl(350,70%,65%)]/8 rounded-full text-xs font-medium text-[hsl(350,70%,50%)]">
        <MessageCircle className="w-3 h-3" />
        {review.product}
      </span>
    </div>
  </div>
);

const SocialProof = () => {
  const [visibleCount, setVisibleCount] = useState(3);

  const handleCTAClick = () => {
    if (typeof window !== 'undefined') {
      if (window.gtag) window.gtag('event', 'whatsapp_click', { location: 'social_proof' });
      if (window.fbq) window.fbq('trackCustom', 'whatsapp_click', { location: 'social_proof' });
    }
    const message = `Hi AuraPrintiqo! 👋\n\nI want to customize a product. Can you guide me?`;
    window.open(
      `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(message)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <section className="py-16 bg-gradient-to-b from-[hsl(var(--cream))] to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-2 bg-amber-100 rounded-full text-sm font-medium text-amber-700 mb-4">
            Customer Love ❤️
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
            Real Reviews from Real Customers
          </h2>

          {/* Aggregate rating */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-2xl font-bold text-gray-900">5.0</span>
            <span className="text-gray-500 text-sm">· 100+ happy customers</span>
          </div>
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-4">
          {testimonials.slice(0, visibleCount).map((review) => (
            <WhatsAppCard key={review.id} review={review} />
          ))}
        </div>

        {/* Show more / CTA */}
        <div className="text-center mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          {visibleCount < testimonials.length && (
            <button
              onClick={() => setVisibleCount(testimonials.length)}
              className="text-sm text-[hsl(350,70%,55%)] font-medium hover:underline"
            >
              Show all {testimonials.length} reviews ↓
            </button>
          )}
          <button
            onClick={handleCTAClick}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold text-sm shadow hover:shadow-lg transition-all hover:scale-105 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
          >
            <MessageCircle className="w-4 h-4" />
            Join 100+ happy customers — Order Now
          </button>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
